import { useMemo, useState } from "react";
import SearchBar from "@/components/SearchBar";
import TrendingQuestions from "@/components/TrendingQuestions";
import FeaturedAnswers from "@/components/FeaturedAnswers";

import Header from "@/components/Header";
import SiteStatsFooter from "@/components/SiteStatsFooter";
import { useJsonLd } from "@/hooks/useJsonLd";
import { useEpisodeCount } from "@/hooks/useEpisodeCount";
import { useSiteStatsSnapshot } from "@/hooks/useSiteStatsSnapshot";

const SITE_URL = "https://askthepods.com";

const HOMEPAGE_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: SITE_URL,
      name: "AskThePods",
      alternateName: "AskThePods — ecommerce podcast search",
      inLanguage: "en",
      description:
        "Ask anything about ecommerce. AskThePods finds the podcast episode that answers it best — Shopify, DTC, Amazon, retail media, subscriptions, CRO, email/SMS, marketplaces.",
      publisher: { "@id": `${SITE_URL}/#org` },
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${SITE_URL}/search?q={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#org`,
      name: "AskThePods",
      url: SITE_URL,
      logo: `${SITE_URL}/favicon.ico`,
      description:
        "Search engine and recommendation service for ecommerce and DTC podcast episodes. Indexes 14,000+ episodes and surfaces the best one for any commerce question with cited answers.",
      knowsAbout: [
        "Ecommerce",
        "Direct-to-consumer (DTC) brands",
        "Shopify",
        "Amazon FBA",
        "Amazon PPC",
        "Retail media",
        "Paid social advertising",
        "Conversion rate optimization",
        "Email and SMS marketing",
        "Subscription commerce",
        "Marketplaces",
        "Customer retention",
        "Influencer marketing",
        "Podcast search",
      ],
      sameAs: [],
    },
    {
      "@type": "SoftwareApplication",
      "@id": `${SITE_URL}/#app`,
      name: "AskThePods",
      url: SITE_URL,
      applicationCategory: "SearchApplication",
      applicationSubCategory: "Podcast search and answer engine",
      operatingSystem: "Web",
      browserRequirements: "Requires JavaScript. Requires HTML5.",
      isAccessibleForFree: true,
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD", availability: "https://schema.org/InStock" },
      featureList: [
        "Search 18,000+ ecommerce podcast episodes",
        "Get cited answers from founders, operators, and experts",
        "See cited clips and sources for every insight",
        "Listen to full episodes instantly",
        "Explore topics across DTC and Shopify brands",
        "Save episodes, create playlists, and take notes",
        "LLM-friendly: structured llms.txt, llms-full.txt, and OpenAPI manifest",
      ],
      audience: {
        "@type": "Audience",
        audienceType: "Ecommerce operators, DTC founders, Shopify merchants, Amazon sellers, retail media buyers, growth marketers",
      },
      provider: { "@id": `${SITE_URL}/#org` },
      sameAs: [],
    },
    {
      "@type": "CollectionPage",
      "@id": `${SITE_URL}/#homepage`,
      url: SITE_URL,
      name: "AskThePods — Google, but for ecommerce podcasts",
      isPartOf: { "@id": `${SITE_URL}/#website`, "@type": "WebSite" },
      about: {
        "@type": "Thing",
        name: "Ecommerce and DTC podcast episode recommendations",
        description:
          "Curated, AI-ranked podcast episode recommendations for ecommerce operators, founders, and marketers. Each result includes a cited answer, confidence score, and direct link to the source episode.",
      },
      audience: {
        "@type": "Audience",
        audienceType: "Ecommerce operators, DTC founders, Shopify merchants, Amazon sellers, retail media buyers, growth marketers",
      },
      keywords:
        "ecommerce podcasts, DTC podcasts, Shopify podcast search, Amazon FBA podcasts, podcast recommendation engine, podcast Q&A, ecommerce expert answers",
      primaryImageOfPage: { "@type": "ImageObject", url: `${SITE_URL}/favicon.ico` },
      mainEntity: { "@id": `${SITE_URL}/#app` },
    },
  ],
};


export default function Index() {
  const [searchFocused, setSearchFocused] = useState(false);
  // Bump a counter on each pick so SearchBar's effect re-fires even for the same prompt.
  const [injected, setInjected] = useState<{ q: string; n: number } | null>(null);
  const { formatted: episodeCount } = useEpisodeCount(14000);
  const { stats } = useSiteStatsSnapshot();

  // Extend the static graph with a Dataset node carrying live counts so AI
  // crawlers can quote concrete numbers (e.g. "AskThePods indexes X episodes
  // across Y shows"). Memoised on the snapshot so we only re-emit on change.
  const homepageJsonLd = useMemo(() => {
    const dataset = {
      "@type": "Dataset",
      "@id": `${SITE_URL}/#episode-index`,
      name: "AskThePods ecommerce podcast episode index",
      description: `A continuously-updated index of ${stats.episodes.toLocaleString("en-US")} ecommerce and DTC podcast episodes from ${stats.podcasts.toLocaleString("en-US")} shows, covering ${stats.topics.toLocaleString("en-US")} commerce topics. Each episode is searchable with AI-ranked recommendations and cited expert answers.`,
      url: SITE_URL,
      keywords: [
        "ecommerce podcasts",
        "DTC podcasts",
        "Shopify",
        "Amazon FBA",
        "podcast search",
      ],
      isAccessibleForFree: true,
      license: SITE_URL,
      creator: { "@id": `${SITE_URL}/#org` },
      includedInDataCatalog: { "@id": `${SITE_URL}/#website` },
      variableMeasured: [
        { "@type": "PropertyValue", name: "Episodes indexed", value: stats.episodes },
        { "@type": "PropertyValue", name: "Podcasts indexed", value: stats.podcasts },
        { "@type": "PropertyValue", name: "Topics covered", value: stats.topics },
        { "@type": "PropertyValue", name: "Published expert answers", value: stats.answers },
      ],
      ...(stats.lastEpisodePublishedAt
        ? { dateModified: stats.lastEpisodePublishedAt }
        : {}),
      size: `${stats.episodes.toLocaleString("en-US")} podcast episodes`,
    };
    return {
      ...HOMEPAGE_JSON_LD,
      "@graph": [...HOMEPAGE_JSON_LD["@graph"], dataset],
    };
  }, [stats]);

  useJsonLd("homepage-website", homepageJsonLd);

  const handlePromptPick = (prompt: string) => {
    setInjected((prev) => ({ q: prompt, n: (prev?.n ?? 0) + 1 }));
  };

  return (
    <div className="min-h-screen flex flex-col retro-grid-bg relative">
      {/* Dark overlay */}
      <div
        className={`fixed inset-0 z-10 bg-foreground/40 backdrop-blur-sm transition-all duration-500 ${
          searchFocused ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setSearchFocused(false)}
      />

      <Header compact />

      {/* Hero */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-6 sm:py-12">
        {/* Title - fades on focus but does NOT collapse, so the search bar
            stays anchored at the same Y-position. Prevents the bar from
            visibly jumping up/down when the user focuses it. */}
        <div
          aria-hidden={searchFocused}
          className={`text-center mt-8 sm:mt-12 mb-5 sm:mb-6 animate-fade-in-up w-full transition-opacity duration-500 ease-out ${
            searchFocused ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
        >
          <h1 className="font-retro text-3xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-2.5 sm:mb-3 leading-tight sm:leading-[1.15] break-words">
            <span className="text-retro-amber">Google</span>, but for
            <br />
            ecommerce podcasts<span className="animate-blink text-primary">_</span>
          </h1>
          <p className="font-mono text-xs sm:text-sm text-muted-foreground max-w-md sm:max-w-lg mx-auto leading-relaxed">
            Search <strong className="font-bold text-foreground">{episodeCount}+</strong> ecommerce and DTC podcast episodes. Get cited answers, expert insights, and the best episodes for your question.
          </p>
        </div>

        {/* Search bar — fixed margin (no focus-driven slide) so it stays
            visually anchored while the title fades and suggestions overlay. */}
        <div
          data-testid="home-search-wrapper"
          className="w-full animate-fade-in-up relative z-20 mt-6 sm:mt-16"
          style={{ animationDelay: '100ms' }}
        >
          <SearchBar
            size="large"
            onFocusChange={setSearchFocused}
            injectedQuery={injected ? `${injected.q}\u0000${injected.n}` : undefined}
          />
        </div>

        {/* Need inspiration? — directly tied to the search bar */}
        <div
          className={`w-full transition-opacity duration-300 ${
            searchFocused ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
        >
          <TrendingQuestions paused={searchFocused} onPromptPick={handlePromptPick} />
        </div>

        {/* Featured published answers — sits between the inspiration prompts
            and the authority paragraph so the strongest social proof appears
            immediately after the search experience.

            Previously animated max-height (0 → 800px), which forces a layout
            recalc on every frame. Switched to opacity + pointer-events so the
            transition is compositor-only and doesn't block the main thread. */}
        <div
          className={`w-full mt-12 sm:mt-16 transition-opacity duration-300 ${
            searchFocused ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
          aria-hidden={searchFocused}
        >
          <FeaturedAnswers />
        </div>
        {/* Spacing wrapper — owns the gap below the teaser (see
            Index.layout.test.tsx). Opacity-only so the animation doesn't
            trigger layout. */}
        <div
          data-testid="home-featured-answers-wrapper"
          className={`w-full mt-12 sm:mt-16 transition-opacity duration-300 ${
            searchFocused ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
          aria-hidden="true"
        />
      </main>

      {/* Footer */}
      <footer className="border-t-[3px] border-foreground py-3 sm:py-4 px-4 space-y-1.5">
        <SiteStatsFooter />
        <p className="text-center text-[10px] sm:text-xs font-mono text-muted-foreground">
          AskThePods · The smartest way to find ecommerce podcast episodes
        </p>
      </footer>
    </div>
  );
}
